import dayjs, { Dayjs } from 'dayjs';
import { getDateFormats } from '../config/dateFormats';

/**
 * Utility functions for date formatting using the centralized configuration
 */

export class DateFormatter {
  private static get formats() {
    return getDateFormats();
  }

  // Date formatting functions
  static formatDate(date: Date | string | Dayjs | null, type: 'display' | 'input' | 'api' | 'short' | 'long' = 'display'): string {
    if (!date) return '';
    const dayjsDate = dayjs(date);
  return dayjsDate.format(DateFormatter.formats.date[type]);
  }

  static formatTime(date: Date | string | Dayjs | null, type: 'display' | 'input' | 'short' | 'long' | 'ampm' = 'display'): string {
    if (!date) return '';
    const dayjsDate = dayjs(date);
  return dayjsDate.format(DateFormatter.formats.time[type]);
  }

  static formatDateTime(date: Date | string | Dayjs | null, type: 'display' | 'input' | 'api' | 'short' | 'long' = 'display'): string {
    if (!date) return '';
    const dayjsDate = dayjs(date);
  return dayjsDate.format(DateFormatter.formats.datetime[type]);
  }

  // Specialized formatting functions
  static formatMonthYear(date: Date | string | Dayjs | null): string {
    if (!date) return '';
    const dayjsDate = dayjs(date);
  return dayjsDate.format(DateFormatter.formats.specialized.monthYear);
  }

  static formatDayMonth(date: Date | string | Dayjs | null): string {
    if (!date) return '';
    const dayjsDate = dayjs(date);
  return dayjsDate.format(DateFormatter.formats.specialized.dayMonth);
  }

  static formatWeekday(date: Date | string | Dayjs | null): string {
    if (!date) return '';
    const dayjsDate = dayjs(date);
  return dayjsDate.format(DateFormatter.formats.specialized.weekday);
  }

  static formatRelativeDays(date: Date | string | Dayjs | null): string {
    if (!date) return '';
    const dayjsDate = dayjs(date);
  return dayjsDate.format(DateFormatter.formats.specialized.relativeDays);
  }

  // Parsing functions
  static parseDate(dateString: string, type: 'display' | 'input' | 'api' = 'display'): Dayjs | null {
    if (!dateString) return null;
  return dayjs(dateString, DateFormatter.formats.date[type]);
  }

  static parseTime(timeString: string, type: 'display' | 'input' = 'display'): Dayjs | null {
    if (!timeString) return null;
  return dayjs(timeString, DateFormatter.formats.time[type]);
  }

  static parseDateTime(dateTimeString: string, type: 'display' | 'input' | 'api' = 'display'): Dayjs | null {
    if (!dateTimeString) return null;
  return dayjs(dateTimeString, DateFormatter.formats.datetime[type]);
  }

  // Validation functions
  static isValidDate(dateString: string, type: 'display' | 'input' | 'api' = 'display'): boolean {
    if (!dateString) return false;
  const parsed = dayjs(dateString, DateFormatter.formats.date[type], true);
    return parsed.isValid();
  }

  static isValidTime(timeString: string, type: 'display' | 'input' = 'display'): boolean {
    if (!timeString) return false;
  const parsed = dayjs(timeString, DateFormatter.formats.time[type], true);
    return parsed.isValid();
  }

  static isValidDateTime(dateTimeString: string, type: 'display' | 'input' | 'api' = 'display'): boolean {
    if (!dateTimeString) return false;
  const parsed = dayjs(dateTimeString, DateFormatter.formats.datetime[type], true);
    return parsed.isValid();
  }

  // Format conversion functions
  static convertDateFormat(
    dateString: string, 
    fromType: 'display' | 'input' | 'api', 
    toType: 'display' | 'input' | 'api'
  ): string {
    if (!dateString) return '';
  const parsed = DateFormatter.parseDate(dateString, fromType);
    if (!parsed) return '';
  return DateFormatter.formatDate(parsed, toType);
  }

  static convertTimeFormat(
    timeString: string, 
    fromType: 'display' | 'input', 
    toType: 'display' | 'input'
  ): string {
    if (!timeString) return '';
  const parsed = DateFormatter.parseTime(timeString, fromType);
    if (!parsed) return '';
  return DateFormatter.formatTime(parsed, toType);
  }

  static convertDateTimeFormat(
    dateTimeString: string, 
    fromType: 'display' | 'input' | 'api', 
    toType: 'display' | 'input' | 'api'
  ): string {
    if (!dateTimeString) return '';
  const parsed = DateFormatter.parseDateTime(dateTimeString, fromType);
    if (!parsed) return '';
  return DateFormatter.formatDateTime(parsed, toType);
  }

  // Helper functions for common use cases
  static toApiFormat(date: Date | string | Dayjs | null): string {
    if (!date) return '';
    const dayjsDate = dayjs(date);
  return dayjsDate.format(DateFormatter.formats.datetime.api);
  }

  static toInputFormat(date: Date | string | Dayjs | null, includeTime: boolean = false): string {
    if (!date) return '';
    const dayjsDate = dayjs(date);
  return dayjsDate.format(includeTime ? DateFormatter.formats.datetime.input : DateFormatter.formats.date.input);
  }

  static toDisplayFormat(date: Date | string | Dayjs | null, includeTime: boolean = false): string {
    if (!date) return '';
    const dayjsDate = dayjs(date);
  return dayjsDate.format(includeTime ? DateFormatter.formats.datetime.display : DateFormatter.formats.date.display);
  }

  // Get format strings (useful for passing to components)
  static getFormat(category: 'date' | 'time' | 'datetime', type: string): string {
  return (DateFormatter.formats[category] as any)[type] || '';
  }

  static getDateFormat(type: 'display' | 'input' | 'api' | 'short' | 'long' = 'display'): string {
  return DateFormatter.formats.date[type];
  }

  static getTimeFormat(type: 'display' | 'input' | 'short' | 'long' | 'ampm' = 'display'): string {
  return DateFormatter.formats.time[type];
  }

  static getDateTimeFormat(type: 'display' | 'input' | 'api' | 'short' | 'long' = 'display'): string {
  return DateFormatter.formats.datetime[type];
  }
}

// Export individual functions for backwards compatibility and convenience
export const formatDate = DateFormatter.formatDate;
export const formatTime = DateFormatter.formatTime;
export const formatDateTime = DateFormatter.formatDateTime;
export const formatMonthYear = DateFormatter.formatMonthYear;
export const formatDayMonth = DateFormatter.formatDayMonth;
export const formatWeekday = DateFormatter.formatWeekday;
export const formatRelativeDays = DateFormatter.formatRelativeDays;

export const parseDate = DateFormatter.parseDate;
export const parseTime = DateFormatter.parseTime;
export const parseDateTime = DateFormatter.parseDateTime;

export const isValidDate = DateFormatter.isValidDate;
export const isValidTime = DateFormatter.isValidTime;
export const isValidDateTime = DateFormatter.isValidDateTime;

export const toApiFormat = DateFormatter.toApiFormat;
export const toInputFormat = DateFormatter.toInputFormat;
export const toDisplayFormat = DateFormatter.toDisplayFormat;

export const getDateFormat = DateFormatter.getDateFormat;
export const getTimeFormat = DateFormatter.getTimeFormat;
export const getDateTimeFormat = DateFormatter.getDateTimeFormat;
